import React from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../albumColors';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Confirmación para "Intercambio rápido".
 *
 * Layout:
 *  - Mobile (< 1024px): bottom sheet con backdrop sutil; max 70vh.
 *  - Desktop (≥ 1024px): drawer anclado a la derecha sin backdrop, así el usuario
 *    puede mirar el álbum mientras revisa los pares sugeridos.
 */
function QuickSwapConfirmSheet({ preview, otherUser, onConfirm, onClose, theme }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const surfaceBg     = theme?.actionBarBg     ?? C.surface;
  const surfaceBorder = theme?.actionBarBorder ?? `${C.outlineVariant}33`;
  const headerText    = theme?.actionBarText   ?? C.onSurface;
  const mutedText     = theme?.mutedText       ?? C.onSurfaceVariant;
  const accent        = theme?.primaryAccent   ?? C.primary;
  if (!preview) return null;

  const pairs = preview.offered.map((g, i) => ({ give: g, want: preview.requested[i] }));
  const wrapperClass = isMobile
    ? 'fixed bottom-0 inset-x-0 max-h-[70vh] rounded-t-2xl flex flex-col pointer-events-auto'
    : 'fixed right-0 top-0 bottom-0 w-[min(440px,42vw)] rounded-l-2xl flex flex-col pointer-events-auto';

  return (
    <div className="fixed inset-0 z-[105] pointer-events-none" role="presentation">
      {isMobile && (
        <div
          className="absolute inset-0 bg-black/30 pointer-events-auto"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        role="dialog"
        aria-modal={isMobile ? 'true' : 'false'}
        aria-label={t('Intercambio rápido')}
        className={`${wrapperClass} shadow-2xl`}
        style={{
          backgroundColor: surfaceBg,
          border: `1px solid ${surfaceBorder}`,
          animation: isMobile ? 'cm-sheet-up 180ms ease-out' : 'cm-drawer-in 220ms ease-out',
        }}
      >
        <style>{`
          @keyframes cm-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
          @keyframes cm-drawer-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>

        <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0 border-b" style={{ borderColor: surfaceBorder }}>
          <h3 className="text-sm font-black uppercase tracking-widest" style={{ color: headerText }}>
            {t('Intercambio rápido')}
          </h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar px-4 py-3">
          {otherUser && (
            <a
              href={otherUser.id ? `/users/${otherUser.id}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 mb-3 -mx-1 px-1 py-1 rounded transition-colors hover:bg-black/5"
              style={{ color: 'inherit', textDecoration: 'none' }}
              title={otherUser.id ? t('Ver perfil') : undefined}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                style={{ backgroundColor: otherUser.color }}
              >{otherUser.initial}</div>
              <span className="text-xs font-bold flex-1" style={{ color: headerText }}>{otherUser.name}</span>
              {otherUser.id && (
                <span className="material-symbols-outlined text-sm" style={{ color: mutedText }}>open_in_new</span>
              )}
            </a>
          )}

          {pairs.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: mutedText }}>
              {t('Sin pares balanceados disponibles')}
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 text-[10px] font-black uppercase tracking-widest">
                <span style={{ color: accent }}>{t('Das')}</span>
                <span style={{ color: mutedText }}>{pairs.length} {t('pares')}</span>
                <span style={{ color: '#F59E0B' }}>{t('Recibís')}</span>
              </div>
              <div className="space-y-1.5">
                {pairs.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span
                      className="flex-1 px-2 py-1.5 rounded text-[12px] font-bold tabular-nums text-right"
                      style={{ backgroundColor: `${accent}22`, color: accent }}
                    >#{p.give}</span>
                    <span className="material-symbols-outlined text-base" style={{ color: mutedText }}>sync_alt</span>
                    <span
                      className="flex-1 px-2 py-1.5 rounded text-[12px] font-bold tabular-nums"
                      style={{ backgroundColor: '#F59E0B22', color: '#B45309' }}
                    >#{p.want}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg p-2 text-center" style={{ backgroundColor: `${accent}14` }}>
                  <div className="font-black" style={{ color: accent }}>{preview.myValueSum}</div>
                  <div style={{ color: mutedText }}>{t('Tu valor total')}</div>
                </div>
                <div className="rounded-lg p-2 text-center" style={{ backgroundColor: '#F59E0B11' }}>
                  <div className="font-black" style={{ color: '#B45309' }}>{preview.theirValueSum}</div>
                  <div style={{ color: mutedText }}>{t('Su valor total')}</div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 px-4 pb-4 pt-2 border-t shrink-0" style={{ borderColor: surfaceBorder }}>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all"
            style={{ color: mutedText, border: `1px solid ${surfaceBorder}` }}
          >
            {t('Cancelar')}
          </button>
          <button
            onClick={onConfirm}
            disabled={pairs.length === 0}
            className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ color: 'white', backgroundColor: accent }}
          >
            <span className="material-symbols-outlined text-sm align-middle">send</span>
            {' '}{t('Confirmar intercambio')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickSwapConfirmSheet;
